// Universal 15-question quiz — replaces the old per-theme questions.
// Q1 is a free-text input. Q2–Q15 are multiple-choice.
// Each question carries the loading-screen copy + duration shown AFTER it is answered.

export type UniversalQuestion =
  | {
      id: string
      kind: 'text'
      title: string
      subtitle?: string
      placeholder: string
      privacyNote: string
      loadingPhrase: string
      loadingDuration: number // ms
    }
  | {
      id: string
      kind: 'multiple'
      title: string
      options: { label: string; value: string }[]
      loadingPhrase: string
      loadingDuration: number // ms
    }

export const universalQuestions: UniversalQuestion[] = [
  // ============ PERSONAL (1–3) ============
  {
    id: 'q1',
    kind: 'text',
    title: 'Antes de comenzar, cuéntale al Oráculo:',
    subtitle:
      '¿Qué es eso que no te deja dormir, que no has podido contarle a nadie y que necesitas entender hoy?',
    placeholder: 'Escribe aquí con total libertad. Nadie más leerá esto...',
    privacyNote:
      '✦ Esta información es estrictamente privada y se usa exclusivamente para calibrar tu lectura.',
    loadingPhrase: 'El Oráculo está registrando tu consulta...',
    loadingDuration: 2500,
  },
  {
    id: 'q2',
    kind: 'multiple',
    title: '¿Cuánto tiempo llevas cargando con esta situación?',
    options: [
      { label: 'Apenas empezó, pero ya me preocupa', value: 'apenas' },
      { label: 'Algunos meses y no encuentro salida', value: 'meses' },
      { label: 'Más de un año y ya estoy agotada', value: 'mas_de_un_anio' },
      {
        label: 'Tanto tiempo que ya no recuerdo cómo me sentía antes',
        value: 'no_recuerdo',
      },
    ],
    loadingPhrase: 'Calculando el peso de tu carga actual...',
    loadingDuration: 2000,
  },
  {
    id: 'q3',
    kind: 'multiple',
    title: '¿Cómo te sientes la mayoría de los días con respecto a esto?',
    options: [
      { label: 'Con miedo de que las cosas empeoren', value: 'miedo' },
      { label: 'Confundida, no sé qué decisión tomar', value: 'confundida' },
      {
        label: 'Resignada, como si ya no hubiera solución',
        value: 'resignada',
      },
      {
        label: 'Con esperanza, pero necesito una señal clara',
        value: 'esperanza',
      },
      { label: 'Paralizada, sin poder avanzar', value: 'paralizada' },
    ],
    loadingPhrase: 'Tu estado emocional ha sido registrado con precisión...',
    loadingDuration: 2000,
  },

  // ============ TRANSICIÓN (4) ============
  {
    id: 'q4',
    kind: 'multiple',
    title:
      '¿Alguna vez has sentido que ciertas situaciones se repiten en tu vida como si siguieran un patrón?',
    options: [
      {
        label:
          'Sí, es como si viviera los mismos problemas con diferentes personas',
        value: 'mismos_problemas',
      },
      {
        label: 'Sí, siento que cuando algo mejora, algo más se rompe',
        value: 'rompe_otra',
      },
      {
        label: 'A veces lo siento pero no le encuentro explicación',
        value: 'a_veces',
      },
      {
        label: 'No lo había pensado así, pero ahora que lo dices...',
        value: 'recien_lo_pienso',
      },
    ],
    loadingPhrase: 'Identificando patrones en tu línea de destino...',
    loadingDuration: 2500,
  },

  // ============ ESPIRITUALES (5–15) ============
  {
    id: 'q5',
    kind: 'multiple',
    title:
      '¿Con qué frecuencia confías en tu intuición para tomar decisiones importantes?',
    options: [
      { label: 'Siempre, mi intuición rara vez me falla', value: 'siempre' },
      { label: 'A veces, pero luego dudo de mí misma', value: 'dudo' },
      { label: 'Casi nunca, prefiero la lógica y los hechos', value: 'logica' },
      {
        label: 'Solía confiar en ella, pero algo la apagó',
        value: 'apagada',
      },
    ],
    loadingPhrase: 'Midiendo la fuerza de tu canal intuitivo...',
    loadingDuration: 2000,
  },
  {
    id: 'q6',
    kind: 'multiple',
    title:
      '¿Has experimentado situaciones que no tienen explicación lógica, como sueños que luego se cumplen o presentimientos que resultaron ser ciertos?',
    options: [
      {
        label: 'Sí, y me inquieta porque no sé qué significa',
        value: 'inquieta',
      },
      {
        label: 'Sí, frecuentemente y ya lo acepto como parte de mí',
        value: 'acepto',
      },
      {
        label: 'Solo algunas veces, pero no les doy importancia',
        value: 'sin_importancia',
      },
      { label: 'No recuerdo haberlo vivido', value: 'no_recuerdo' },
    ],
    loadingPhrase: 'Registrando tu historial de señales recibidas...',
    loadingDuration: 2000,
  },
  {
    id: 'q7',
    kind: 'multiple',
    title: '¿Cómo percibes el concepto del destino en tu vida?',
    options: [
      {
        label: 'Creo que todo está escrito y hay poco que pueda cambiar',
        value: 'escrito',
      },
      {
        label: 'Creo que el destino existe pero las decisiones lo moldean',
        value: 'moldear',
      },
      {
        label: 'No sé si creer en él, pero algo me dice que no es casualidad',
        value: 'no_casualidad',
      },
      {
        label: 'Creo que cada uno construye su propio camino sin guía externa',
        value: 'construir',
      },
    ],
    loadingPhrase: 'Analizando tu relación con el flujo del destino...',
    loadingDuration: 2000,
  },
  {
    id: 'q8',
    kind: 'multiple',
    title:
      '¿Has consultado antes con algún tipo de guía espiritual, tarot, astrología o lectura similar?',
    options: [
      {
        label: 'Sí, varias veces y algunas me han dado claridad real',
        value: 'claridad',
      },
      {
        label: 'Sí, pero nunca sentí que fuera verdaderamente preciso',
        value: 'impreciso',
      },
      { label: 'Solo una o dos veces por curiosidad', value: 'curiosidad' },
      { label: 'Nunca, esta es mi primera vez', value: 'primera_vez' },
    ],
    loadingPhrase:
      'El Oráculo está comparando tu perfil con registros anteriores...',
    loadingDuration: 3000,
  },
  {
    id: 'q9',
    kind: 'multiple',
    title:
      '¿Sientes que hay algo o alguien en tu vida que está bloqueando tu avance, aunque no puedas identificarlo claramente?',
    options: [
      {
        label: 'Sí, claramente siento una presencia o energía que me frena',
        value: 'presencia',
      },
      {
        label: 'Sí, pero creo que el bloqueo viene de mí misma',
        value: 'bloqueo_propio',
      },
      {
        label: 'A veces lo siento pero no sé si es real o es mi mente',
        value: 'mi_mente',
      },
      {
        label: 'No lo siento así, pero sí noto que las cosas no fluyen',
        value: 'no_fluyen',
      },
    ],
    loadingPhrase: 'Detectando interferencias en tu campo energético...',
    loadingDuration: 2500,
  },
  {
    id: 'q10',
    kind: 'multiple',
    title:
      'Cuando piensas en tu situación actual, ¿qué imagen o sensación aparece primero en tu mente?',
    options: [
      { label: 'Un laberinto sin salida visible', value: 'laberinto' },
      {
        label: 'Una puerta cerrada que no encuentro cómo abrir',
        value: 'puerta_cerrada',
      },
      { label: 'Una tormenta que no termina de pasar', value: 'tormenta' },
      {
        label: 'Un camino largo y solitario sin señales claras',
        value: 'camino_solitario',
      },
      {
        label: 'Una llama pequeña que todavía no se apaga',
        value: 'llama',
      },
    ],
    loadingPhrase: 'Interpretando los símbolos de tu mente subconsciente...',
    loadingDuration: 3000,
  },
  {
    id: 'q11',
    kind: 'multiple',
    title: '¿Qué rol sueles ocupar en tus relaciones más importantes?',
    options: [
      {
        label: 'El pilar: todos me necesitan y yo cargo con todo',
        value: 'pilar',
      },
      { label: 'La que da más de lo que recibe', value: 'da_mas' },
      {
        label: 'La que espera en silencio que las cosas mejoren',
        value: 'espera',
      },
      {
        label: 'La que lucha, pero siente que lucha sola',
        value: 'lucha_sola',
      },
      {
        label: 'La que se perdió a sí misma en el camino',
        value: 'se_perdio',
      },
    ],
    loadingPhrase: 'Trazando tu arquetipo dentro del mapa del destino...',
    loadingDuration: 2000,
  },
  {
    id: 'q12',
    kind: 'multiple',
    title:
      'Si el Oráculo pudiera darte UNA respuesta hoy, ¿cuál sería la pregunta más honesta que le harías?',
    options: [
      {
        label: '¿Esta persona realmente me ama o debo soltar?',
        value: 'amor',
      },
      {
        label: '¿Voy por el camino correcto o debo cambiar algo urgente?',
        value: 'camino',
      },
      {
        label: '¿Cuándo va a mejorar mi situación económica?',
        value: 'economia',
      },
      {
        label: '¿Hay algo que deba saber y que nadie me ha dicho?',
        value: 'secreto',
      },
      {
        label: '¿Qué es lo que me está impidiendo ser feliz de verdad?',
        value: 'felicidad',
      },
    ],
    loadingPhrase:
      'Esta pregunta ha sido registrada en el núcleo de tu lectura...',
    loadingDuration: 3500,
  },
  {
    id: 'q13',
    kind: 'multiple',
    title:
      '¿Cuándo fue la última vez que sentiste que todo estaba bien y fluía con naturalidad?',
    options: [
      {
        label: 'Hace mucho tiempo, ya casi no recuerdo esa sensación',
        value: 'hace_mucho',
      },
      {
        label: 'Hubo un momento específico que cambió todo',
        value: 'momento_especifico',
      },
      { label: 'En momentos puntuales, pero no dura', value: 'puntual' },
      {
        label: 'Honestamente, no recuerdo haberlo sentido así',
        value: 'nunca',
      },
    ],
    loadingPhrase: 'Localizando el punto de inflexión en tu línea temporal...',
    loadingDuration: 2000,
  },
  {
    id: 'q14',
    kind: 'multiple',
    title:
      '¿Qué tan lista estás para recibir una verdad que podría cambiar la dirección de tu vida?',
    options: [
      {
        label: 'Completamente lista, es exactamente lo que vine a buscar',
        value: 'lista',
      },
      {
        label: 'Lista, aunque me da un poco de miedo lo que pueda revelar',
        value: 'miedo',
      },
      { label: 'Necesito saberlo aunque duela', value: 'aunque_duela' },
      {
        label: 'No sé si estoy lista, pero algo me trajo hasta aquí',
        value: 'algo_me_trajo',
      },
    ],
    loadingPhrase: 'Preparando el nivel de profundidad de tu lectura final...',
    loadingDuration: 2500,
  },
  {
    id: 'q15',
    kind: 'multiple',
    title:
      '¿Cuánto tiempo más estás dispuesta a vivir sin la claridad que mereces?',
    options: [
      {
        label: 'Ya no más. Llegué aquí porque necesito respuestas hoy',
        value: 'respuestas_hoy',
      },
      {
        label: 'He esperado demasiado, algo tiene que cambiar ya',
        value: 'esperado_demasiado',
      },
      {
        label: 'No lo sé, pero siento que este es el momento',
        value: 'es_el_momento',
      },
      {
        label: 'Tengo miedo, pero el no saber ya me cuesta más que el saber',
        value: 'no_saber_cuesta_mas',
      },
    ],
    loadingPhrase:
      'Compilando todas tus respuestas para generar tu lectura única e irrepetible...',
    loadingDuration: 4500,
  },
]
